import { DataPointsApiResponse } from '@tableau/metrics-engine';
import { TextLinkWidget } from '@tableau/widgets';
import * as queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { showActionableToast } from '~commonInfra/components/ActionableMessage';
import { DeprecatedInterpolatedString } from '~commonInfra/components/InterpolatedString';
import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { UrlParamKeys } from '~commonInfra/url/UrlParamKeys';
import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { CommonErrorCode } from '~commonInfra/utils/ErrorCodes';
import { Helpers } from '~commonInfra/utils/Helpers';
import { setPreviousUrl } from '~src/bootstrap/redux/actions/SetPreviousUrlReducerAction';
import { addContentFavorite, removeContentFavorite } from '~src/favorites/actions/FavoritesActions';
import { RequestAccessAction } from '~src/requestAccess/actions/RequestAccessAction';
import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import { setSpaceInfoItem } from '~src/spacesPlatform/actions/EnterSpaceAction';
import {
  addContentTypeToProjectHierarchy,
  populateContentBreadcrumbs
} from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbHelpers';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { SpaceInfo, SpaceItemForNonEntitySpace } from '~src/spacesPlatform/state/SpaceInfo';
import { WorkbookUsageMetricsResourceQuery } from '~src/usageMetrics/resources/UsageMetrics';
import { WorkbooksResource } from '~src/workbook/resources/Workbooks';

import { SessionSiteSelectors } from '../../../bootstrap/redux/selectors/SessionInfoSelectors';
import { AppState } from '../../../bootstrap/redux/state/AppState';
import { getAppStore } from '../../../bootstrap/redux/store/AppStore';
import { RouterError } from '../../../bootstrap/routerHelpers/RouterError';
import { Messages } from '../../../codegen/Localize';
import { BrowserTitleService } from '../../../commonInfra/services/BrowserTitleService';
import { HelpService } from '../../../commonInfra/services/HelpService';
import { ProjectsResource } from '../../../projects/resources/Projects';
import { IFavoritableItemInfo } from '../../../spacesPlatform/models/resourceInfoParts';
import { replaceMetricDataRefreshList, resumeMetric, updateMetricsDataByIds } from '../../actions/UpdateMetricsActions';
import { IMetricInfo, IMetricInfoWithoutData, isUsageMetricInfo } from '../../models/IMetricInfo';
import { Metrics } from '../../resources/Metrics';
import { selectMetricDataForMetricId, userCanResumeSuspendedMetric, userIsMetricOwner } from '../../selectors';
import { MetricDetailSpaceContent } from './MetricDetailSpaceContent';

const DETAIL_VIEW_MIN_HEIGHT = 320;
const DETAIL_VIEW_MAX_HEIGHT = 700;

export interface MetricDetailSpaceOwnProps {
  readonly urls: SpacesSiteUrls;
  readonly siteName: string;
  readonly id: string;
  readonly routeLocationSearch: string;
  readonly grantAccessDisplay: boolean;
  readonly grantAccessUrl: string;
  readonly renderContent: (state: MetricDetailSpaceState, metricDataProp: DataPointsApiResponse, detailHolder: React.RefObject<HTMLDivElement>, addFavorite: () => void, removeFavorite: () => void, urls: SpacesSiteUrls, metadataChanged?: () => void, isSpaceForWorkbookUsageMetric?: boolean) => JSX.Element;
  readonly userCanResumeMetric: (metricInfo: IMetricInfo) => boolean;
  readonly isMetricOwner: (metricInfo: IMetricInfo) => boolean;
  readonly showResumeMetricWarning: (metricInfo: IMetricInfo, isOwner: boolean,
    resumeMetricHelpLink: string, resumeMetric: (metricInfo: IMetricInfo, isOwner: boolean) => void) => void;
  readonly showSuspendedMetricWarning: () => void;
  readonly projects: ProjectsResource;
}

export interface MetricDetailSpaceForWorkbookUsageMetricProps {
  readonly workbooks: WorkbooksResource;
  readonly workbookId: string;
}

export interface MetricDetailSpaceDispatchProps {
  readonly addContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => any;
  readonly setSpaceInfoItem: (spaceInfo: SpaceInfo) => void;
  readonly resumeMetric: (metricInfo: IMetricInfo, isOwner: boolean) => void;
  readonly updateMetricsDataByIds: (ids: string[]) => void;
  readonly replaceMetricDataRefreshList: (ids: string[]) => void;
  readonly setPreviousUrl: (previousUrl: string) => void;
}

export interface MetricDetailSpaceStateProps {
  readonly isRequestAccessEnabled: boolean;
  readonly metricData?: DataPointsApiResponse;
}

export interface MetricDetailSpaceState {
  readonly metricInfo: IMetricInfoWithoutData;
  readonly hasError: boolean;
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly errorCode: number;
  readonly detailHeight: number;
}

const InitialState: MetricDetailSpaceState = {
  metricInfo: null,
  hasError: false,
  initialRender: true,
  renderChildren: false,
  errorCode: undefined,
  detailHeight: DETAIL_VIEW_MIN_HEIGHT,
};

export type MetricDetailSpaceProps = MetricDetailSpaceOwnProps & Partial<MetricDetailSpaceForWorkbookUsageMetricProps> & MetricDetailSpaceDispatchProps & MetricDetailSpaceStateProps;

export class MetricDetailSpace extends React.PureComponent<MetricDetailSpaceProps, MetricDetailSpaceState> {
  private unmounted: boolean = false;
  private spaceInfo: SpaceInfo = undefined;
  private detailHolder: React.RefObject<HTMLDivElement>;

  public constructor(props: MetricDetailSpaceProps) {
    super(props);
    this.state = InitialState;
    this.spaceInfo = { spaceType: 'metric', subspaceName: undefined, item: SpaceItemForNonEntitySpace /* populated after fetching data */ };
    this.detailHolder = React.createRef();

    if (!this.isSpaceForWorkbookUsageMetric()) {
      // usage metrics have a separate API where the data points are already provided, so no need to call updateMetricsDataByIds & replaceMetricDataRefreshList
      if (!this.props.metricData) {
        // go fetch data, we weren't provided any.
        props.updateMetricsDataByIds([this.props.id]);
      }
      if (FeatureFlags.metricsAutoUpdateEnabled) {
        props.replaceMetricDataRefreshList([this.props.id]);
      }
    }
  }

  private updateDetailHeight() {
    if (!this.detailHolder.current) {
      return;
    }

    const desiredHeight = this.detailHolder.current.getBoundingClientRect().height;
    const resolvedHeight = Math.max(Math.min(desiredHeight, DETAIL_VIEW_MAX_HEIGHT), DETAIL_VIEW_MIN_HEIGHT);
    this.setState({
      detailHeight: resolvedHeight,
    });
  }

  private windowSizeChanged = () => {
    this.updateDetailHeight();
  };

  private isSpaceForWorkbookUsageMetric = () => {
    // check the props under MetricDetailSpaceForWorkbookUsageMetricProps exist
    return this.props.workbooks !== undefined && this.props.workbookId !== undefined;
  };

  public componentDidMount() {
    this.unmounted = false;
    this.fetchMetric();
    window.addEventListener('resize', this.windowSizeChanged);
    this.updateDetailHeight();
  }

  public componentWillUnmount() {
    this.unmounted = true;
    window.removeEventListener('resize', this.windowSizeChanged);
  }

  public componentDidUpdate(prevProps: MetricDetailSpaceProps) {
    this.updateDetailHeight();
    if (this.changedMetric(prevProps)) {
      if (!this.isSpaceForWorkbookUsageMetric()) {
        // usage metrics have a separate API where the data points are already provided, so no need to call updateMetricsDataByIds
        this.props.updateMetricsDataByIds([this.props.id]);
      }
      this.updateState(InitialState /* want the initial state for the metric */);
      this.fetchMetric();
      return;
    }

    // if we don't have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender && this.state.metricInfo) {
      if(!this.props.metricData){
        this.updateState({ hasError: true, renderChildren: false });
      }
      this.updateState({ initialRender: false, renderChildren: true });

      if (!this.isSpaceForWorkbookUsageMetric()) {
        // Update recently viewed and the metric view count
        Metrics.markRecentlyViewed(this.state.metricInfo.id);
        Metrics.markViewed(this.state.metricInfo.id);
      }

      // Show warning toast if metric is suspended
      const values = queryString.parse(this.props.routeLocationSearch);
      const isResumeBannerSuppressed = Helpers.BooleanQueryParamRegex.test(values[UrlParamKeys.SuppressResumeMetricBanner]);
      if (this.state.metricInfo.suspended && !isResumeBannerSuppressed) {
        this.props.userCanResumeMetric(this.state.metricInfo) ?
          this.props.showResumeMetricWarning(this.state.metricInfo,
            this.props.isMetricOwner(this.state.metricInfo),
            HelpService.getServerHelpUrlByCategory('metrics-suspend'),
            this.props.resumeMetric) :
          this.props.showSuspendedMetricWarning();
      }

      this.setState({initialRender: false, renderChildren: true}, () => {
        if (this.props.grantAccessDisplay) {
          RequestAccessAction.handleGrantAccess(this.state.metricInfo, this.props.grantAccessUrl);
          this.resetPreviousUrl();
        }
      });
    }
  }

  private updateState<K extends keyof MetricDetailSpaceState>(newState: Pick<MetricDetailSpaceState, K>): void {
    if (!this.unmounted) {
      this.setState(newState, this.updateTitle);
    }
  }

  private updateTitle() {
    if (this.state.metricInfo) {
      BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: this.state.metricInfo.name });
    } else {
      BrowserTitleService.setTitleToServerName();
    }
  }

  private changedMetric(prevProps: MetricDetailSpaceProps) {
    return !_.isUndefined(this.props.siteName) &&
      !_.isUndefined(this.props.id) &&
      (prevProps.siteName !== this.props.siteName || prevProps.id !== this.props.id);
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.metricInfo);
  }

  private fetchMetric = async () => {
    try {
      if (this.isSpaceForWorkbookUsageMetric()) {
        const workbookUsageMetricsList = await new WorkbookUsageMetricsResourceQuery().withExtraParams({ workbookId: this.props.workbookId }).getSlice(0, 0);
        const metric = workbookUsageMetricsList.items.find(m => m.id === this.props.id);
        if (workbookUsageMetricsList.errors?.length || !metric) {
          // API error repsonse or doesn't return the metric we need
          this.updateState({ hasError: true, renderChildren: false });
        } else {
          this.updateState({ metricInfo: metric });

          // update breadcrumbs to be Explore > Project > Workbook > Usage Metric
          const workbook = await this.props.workbooks.getById(this.props.workbookId);
          const projectHierarchy = await this.props.projects.getProjectHierarchy(workbook.project.id);
          const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
          populateContentBreadcrumbs([...projectHierarchyWithType, workbook, metric], this.props.urls);
        }
      } else {
        const metric: IMetricInfoWithoutData = await Metrics.getById(this.props.id);
        if (metric) {
          this.spaceInfo = { item: { ...metric, contentType: 'metric', id: this.props.id }, spaceType: 'metric', subspaceName: undefined };
          this.props.setSpaceInfoItem(this.spaceInfo);
          this.updateState({ metricInfo: metric });

          const projectHierarchy = await this.props.projects.getProjectHierarchy(metric.project.id);
          const projectHierarchyWithType = addContentTypeToProjectHierarchy(projectHierarchy);
          populateContentBreadcrumbs([...projectHierarchyWithType, metric], this.props.urls);
        } else {
          this.updateState({ hasError: true, renderChildren: false });
        }
      }
    } catch (e) {
      this.updateState({ hasError: true, renderChildren: false, errorCode: e as number });
    }
  };

  private addFavorite = () => {
    if (this.state.metricInfo) {
      this.updateState({ metricInfo: { ...this.state.metricInfo, favorite: true }});
      this.props.addContentFavorite(this.state.metricInfo);
    }
  };

  private removeFavorite = () => {
    if (this.state.metricInfo) {
      this.updateState({ metricInfo: { ...this.state.metricInfo, favorite: false }});
      this.props.removeContentFavorite(this.state.metricInfo);
    }
  };

  private metadataChanged = () => {
    this.fetchMetric();
  };

  public render() {
    let element: JSX.Element;
    if (this.state.renderChildren) {
      const isSpaceForWorkbookUsageMetric = this.isSpaceForWorkbookUsageMetric();
      const metricData = isSpaceForWorkbookUsageMetric && isUsageMetricInfo(this.state.metricInfo)
        ? this.state.metricInfo.dataPointsApiResponse
        : this.props.metricData;
      element = this.props.renderContent(this.state, metricData, this.detailHolder, this.addFavorite, this.removeFavorite, this.props.urls, this.metadataChanged, isSpaceForWorkbookUsageMetric);
    } else if (this.state.hasError) {
      if (this.props.isRequestAccessEnabled) {
        if (FeatureFlags.requestAccessDialogEnabled && (this.state.errorCode === CommonErrorCode.CallingUserInsufficientPermissions)) {
          return <Redirect to={`${this.props.urls.homePath()}${this.props.urls.metricRequestAccessPath(this.props.id)}`} />;
        } else {
          return <RequestAccessError url={this.props.urls.exploreUrl()} contentIdentifier={this.props.id} contentType={'metric'} errorCode={this.state.errorCode} />;
        }
      }
      element = <RouterError errorType={'metricDetail'} urls={this.props.urls} />;
    } else {
      element = null;
    }

    return (
      <ConnectedSpaceInfoProvider spaceInfo={this.spaceInfo}>
        {element}
      </ConnectedSpaceInfoProvider>);
  }

  private resetPreviousUrl = () => {
    const cleanDSUrls = window.location.href;
    this.props.setPreviousUrl(cleanDSUrls);
  };
}

export function renderMetricDetailSpaceContent(state: MetricDetailSpaceState, metricDataProp: DataPointsApiResponse, detailHolder: React.RefObject<HTMLDivElement>, addFavorite: () => void, removeFavorite: () => void, urls: SpacesSiteUrls, metadataChanged?: () => void, isSpaceForWorkbookUsageMetric?: boolean): React.ReactElement {
  return <MetricDetailSpaceContent {...{state, addFavorite, removeFavorite, detailHolder, metricDataProp, urls, metadataChanged, isSpaceForWorkbookUsageMetric }} />;
}

export function userCanResumeMetric(metricInfo: IMetricInfo) {
  return userCanResumeSuspendedMetric(getAppStore().getState(), metricInfo);
}

export function isMetricOwner(metricInfo: IMetricInfo) {
  return userIsMetricOwner(getAppStore().getState(), metricInfo);
}

export function showResumeMetricWarning(metricInfo: IMetricInfo, isOwner: boolean,
   resumeMetricHelpLink: string, resumeMetric: (metricInfo: IMetricInfo, isOwner: boolean) => void) {
  const learnMoreLink = (
    <TextLinkWidget
      href={resumeMetricHelpLink}
      target="_blank"
      testId="metrics-resume-help"
    >
      {Messages.learnMore()}
    </TextLinkWidget>
  );

  const suspendedMetricString = (
    <DeprecatedInterpolatedString
      baseStringKey="metricSuspendedWithResumeTooltip"
      translate={() => Messages.metricSuspendedWithResumeTooltip({LEARN_MORE_LINK: '{LEARN_MORE_LINK}'})}
      replacements={{
        'LEARN_MORE_LINK': () => learnMoreLink,
      }}
    />
  );

  showActionableToast({
    // We leave the message prop empty because our string is wrapped in a
    // InterpolatedString component which we pass to the children prop.
    message: '',
    children: suspendedMetricString,
    testId: `suspended-metric-warning`,
    actionMessage: Messages.metricResumeButtonLabel(),
    action: () => resumeMetric(metricInfo, isOwner)
  }, ToastType.Warning);
}

export function showSuspendedMetricWarning() {
  const service = ToasterService.instance;
  service.pop({
    type: ToastType.Warning,
    text: Messages.metricSuspendedNoResumeTooltip(),
    testId: `suspended-metric-nonowner-warning`
  });
}

const mapDispatchToProps: MapDispatchToProps<MetricDetailSpaceDispatchProps> = (dispatch) => bindActionCreators({
  addContentFavorite: addContentFavorite,
  removeContentFavorite: removeContentFavorite,
  setSpaceInfoItem: setSpaceInfoItem,
  resumeMetric: resumeMetric,
  updateMetricsDataByIds: updateMetricsDataByIds,
  replaceMetricDataRefreshList: replaceMetricDataRefreshList,
  setPreviousUrl: setPreviousUrl,
}, dispatch);

const mapStateToProps: MapStateToProps<MetricDetailSpaceStateProps> = (state: AppState, ownProps: MetricDetailSpaceProps) => ({
  isRequestAccessEnabled: SessionSiteSelectors.isRequestAccessEnabled(state),
  metricData: selectMetricDataForMetricId(state, ownProps.id),
});

type ConnectedMetricDetailSpaceProps = MetricDetailSpaceOwnProps & Partial<MetricDetailSpaceForWorkbookUsageMetricProps>;

export const ConnectedMetricDetailSpace: React.ComponentClass<ConnectedMetricDetailSpaceProps>
  = connect(mapStateToProps, mapDispatchToProps)(MetricDetailSpace);
